import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {CoreStateType} from "./types";
import {
    CtrBureaux,
    CtrCaracteristiques,
    CtrDynamicCustomInput,
    CtrDynamicTables,
    CtrFirmes,
    CtrGenresProprietes,
    CtrInscriptions,
    CtrMembres,
    CtrMetaField,
    CtrMetaFieldInput,
    CtrMunicipalites,
    CtrPhotos,
    CtrQuartiers,
    CtrRegions,
    CtrSousTypeCaracteristiques,
    CtrStaticCustomInput,
    CtrStaticTables,
    CtrTypeCaracteristiques,
    CtrTypesBannieres,
    CtrValeursFixes,
    ReactText,
    WebsiteConfig
} from "../../graphql/generated/graphql-main.tsx";
import {WritableDraft} from "immer";

type IndexableTableEntry = WritableDraft<Record<string, unknown> & {
    CUSTOM_FR?: string | null;
    CUSTOM_EN?: string | null;
}>;

const initialState: CoreStateType = {
    site_config: {},
    react_texts: [],
    meta_fields: [],
    thumbnails: [],
    some_chars: [],
    ev_offices: {},
    [CtrDynamicTables.Inscriptions]: [],
    [CtrDynamicTables.Photos]: [],
    [CtrDynamicTables.Firmes]: [],
    [CtrDynamicTables.Bureaux]: [],
    [CtrDynamicTables.Membres]: [],
    [CtrStaticTables.GenresProprietes]: [],
    [CtrStaticTables.Municipalites]: [],
    [CtrStaticTables.Quartiers]: [],
    [CtrStaticTables.Regions]: [],
    [CtrStaticTables.SousTypeCaracteristiques]: [],
    [CtrStaticTables.TypeCaracteristiques]: [],
    [CtrStaticTables.TypesBannieres]: [],
    [CtrStaticTables.ValeursFixes]: [],
}

export const coreSlice = createSlice({
    name: "core-slice",
    initialState: initialState,
    reducers: {
        $setSiteConfig(s, a: PayloadAction<WebsiteConfig>) {
            s.site_config = a.payload;
        },
        $setReactTexts(s, a: PayloadAction<ReactText[]>) {
            s.react_texts = a.payload;
        },
        $setMetaFields(s, a: PayloadAction<CtrMetaField[]>) {
            s.meta_fields = a.payload;
        },
        $updateMetaField(s, a: PayloadAction<CtrMetaFieldInput>) {
            const _field = s.meta_fields.find(it =>
                it.table == a.payload.table && it.name == a.payload.name);
            if (_field == null) return;

            if (a.payload.label_fr != null)
                _field.label_fr = a.payload.label_fr;
            if (a.payload.label_en != null)
                _field.label_en = a.payload.label_en;
        },
        $setThumbnails(s, a: PayloadAction<CtrPhotos[]>) {
            s.thumbnails = a.payload;
        },
        $setSomeChars(s, a: PayloadAction<CtrCaracteristiques[]>) {
            s.some_chars = a.payload;
        },
        $setEvOffices(s, a: PayloadAction<{ [code: string]: string }>) {
            s.ev_offices = a.payload;
        },

        /** static tables */

        $setGenresProprietes(s, a: PayloadAction<CtrGenresProprietes[]>) {
            s.GENRES_PROPRIETES = a.payload;
        },
        $setMunicipalites(s, a: PayloadAction<CtrMunicipalites[]>) {
            s.MUNICIPALITES = a.payload;
        },
        $setQuartiers(s, a: PayloadAction<CtrQuartiers[]>) {
            s.QUARTIERS = a.payload;
        },
        $setRegions(s, a: PayloadAction<CtrRegions[]>) {
            s.REGIONS = a.payload;
        },
        $setSousTypeCaracteristiques(s, a: PayloadAction<CtrSousTypeCaracteristiques[]>) {
            s.SOUS_TYPE_CARACTERISTIQUES = a.payload;
        },
        $setTypeCaracteristiques(s, a: PayloadAction<CtrTypeCaracteristiques[]>) {
            s.TYPE_CARACTERISTIQUES = a.payload;
        },
        $setTypesBannieres(s, a: PayloadAction<CtrTypesBannieres[]>) {
            s.TYPES_BANNIERES = a.payload;
        },
        $setValeursFixes(s, a: PayloadAction<CtrValeursFixes[]>) {
            s.VALEURS_FIXES = a.payload;
        },
        $updateStaticEntry(s, a: PayloadAction<CtrStaticCustomInput>) {
            console.info(`payload`, a.payload);
            const {table, keys, vals, custom_fr, custom_en} = a.payload;
            const tableEntries = s[table as keyof CoreStateType];
            if (!Array.isArray(tableEntries)) return;

            const _entries = tableEntries as IndexableTableEntry[];
            const _entry = _entries.find(it => {
                // most reliable way to check multiple dynamic conditions on single entry
                const _conditions: boolean[] = [];
                keys.forEach((key, idx) => {
                    if (key == null) return;
                    _conditions.push(it[key] == vals[idx])
                });
                return _conditions.every(it => it);
            }) ?? null;

            if (_entry == null) return;
            if (custom_fr != undefined)
                _entry.CUSTOM_FR = custom_fr;
            if (custom_en != undefined)
                _entry.CUSTOM_EN = custom_en;
        },
        $updateDynamicEntry(s, a: PayloadAction<CtrDynamicCustomInput>) {
            console.info(`payload`, a.payload);
            const {table, filter_keys, filter_vals, custom_kvs} = a.payload;
            const tableEntries = s[table as keyof CoreStateType];
            if (!Array.isArray(tableEntries)) return;

            const _entries = tableEntries as IndexableTableEntry[];
            const _entry = _entries.find(it => {
                // the most reliable way to check multiple dynamic conditions on a single entry
                const _conditions: boolean[] = [];
                filter_keys.forEach((key, idx) => {
                    if (key == null) return;
                    _conditions.push(it[key] == filter_vals[idx])
                });
                return _conditions.every(it => it);
            }) ?? null;

            if (_entry == null) return;
            // may update multiple key:value pairs, so need to loop over them
            if (custom_kvs != undefined) {
                for (const kval of custom_kvs) {
                    if (kval?.custom_key == null) continue;
                    _entry[kval.custom_key] = kval.custom_value;
                }
            }
        },

        /** dynamic tables */

        $setFirmes(s, a: PayloadAction<CtrFirmes[]>) {
            s.FIRMES = a.payload;
        },
        $setBureaux(s, a: PayloadAction<CtrBureaux[]>) {
            s.BUREAUX = a.payload;
        },
        $setMembres(s, a: PayloadAction<CtrMembres[]>) {
            s.MEMBRES = a.payload;
        },
        $setInscriptions(s, a: PayloadAction<CtrInscriptions[]>) {
            s.INSCRIPTIONS = a.payload;
        },
        $setPhotos(s, a: PayloadAction<CtrPhotos[]>) {
            s.PHOTOS = a.payload;
        }
    }
});

// export the actions to be `dispatched`
export const coreActions = coreSlice.actions;
